import { useState, useEffect } from "react";
import { API_URL } from "constants.js";
import axios from "axios";
import { useParams } from "react-router-dom";
import { TextField, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { CustomField } from "types/CustomField";
import { Product } from "types/Product";
import DatePickerx from "features/datepicker/date-picker";
import { useAppDispatch } from "hooks";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchById } from "services/upload-file.service";
import { fetchUserDetails } from "containers/user/user-container.slice";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";
import { fetchBusinessTripDetails } from "containers/business-trip-view-container/business-trip-container.slice";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import { useSignerSelection } from "../hooks/useSignerSelection";
import { useCustomFields } from "../hooks/useCustomFields";
import CustomFieldsRenderer from "../components/CustomFieldsRenderer";
import DocumentFormShell from "../components/DocumentFormShell";
import {
  validateForm,
  getParentTemplateMismatchErrorKey,
  buildPayload,
  BuildPdfFormProps,
  normalizeBuildPdfTemplateId,
  resolveEditBranch,
} from "../build-pdf-utils";
import { submitDocument, updateDocument } from "../build-pdf-api";

export default function BusinessTripDocumentForm({
  customerId,
  isCustomerApproved = false,
  template,
  fileId,
  assignedUserId,
  id,
  subject,
  user,
  signType: signTypeProps,
  managerSigner,
  businessTrip,
  businessTripFileType,
  lockedTemplateId,
}: BuildPdfFormProps) {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();

  const [templateId, setTemplateId] = useState(lockedTemplateId || "");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [fileTypeContext, setFileTypeContext] = useState("");
  const [templateIdByFetch, setTemplateIdByFetch] = useState("");
  const [businessTripId, setBusinessTripId] = useState<any>(null);

  const [systemTemplateFields, setSystemTemplateFields] = useState<
    CustomField[] | null
  >(null);
  const [systemTemplateSource, setSystemTemplateSource] = useState<
    string | null
  >(null);
  const [loadingSystemFields, setLoadingSystemFields] = useState(false);
  const [isEditingSystemTemplate, setIsEditingSystemTemplate] = useState(false);

  const isBusinessTripCreateFlow =
    !id && !!businessTrip && !!businessTripFileType;
  const isSystemSource =
    isBusinessTripCreateFlow && systemTemplateSource === "system";
  const hasSystemFields =
    isSystemSource && (systemTemplateFields?.length ?? 0) > 0;
  const isAutoBusinessTripDocument = isSystemSource && !hasSystemFields;

  const {
    selectedUser,
    selectedContactIds,
    signType,
    setSignType,
    selectedNonTechUser,
    selectedTabUser,
    hydrateSigners,
  } = useSignerSelection({
    signTypeProps,
    managerSigner,
    assignedUserId,
    isEditMode: !!id,
  });

  const {
    customFields,
    setCustomFields,
    templateEntity,
    setTemplateEntity,
    fileEntitiesMap,
    handleCustomFieldChange,
    transformCustomFields,
    setInitialCustomFieldsWhenEditLoaded,
    setInitialTemplateEntityWhenEditLoaded,
  } = useCustomFields({
    companyId,
    templateId,
    templateIdByFetch,
    isEditMode: !!id,
    onSignTypeDetected: setSignType,
  });

  useEffect(() => {
    if (lockedTemplateId && !id) setTemplateId(lockedTemplateId);
  }, [lockedTemplateId]);

  useEffect(() => {
    if (businessTrip) setBusinessTripId(businessTrip);
  }, [businessTrip]);

  useEffect(() => {
    if (!isBusinessTripCreateFlow || !companyId || !businessTripFileType)
      return;
    let cancelled = false;
    setLoadingSystemFields(true);
    axios
      .get(
        `${API_URL}/${companyId}/system-template-fields/${businessTripFileType}`
      )
      .then((res) => {
        if (cancelled) return;
        const { fields = [], source } = res.data;
        if (source === "custom") {
          setSystemTemplateSource("custom");
          setSystemTemplateFields([]);
        } else {
          setSystemTemplateSource("system");
          setSystemTemplateFields(fields);
          if (fields.length > 0) {
            setCustomFields(
              fields.map((f: any) => ({
                ...f,
                value: f.isMulti ? [] : "",
              }))
            );
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSystemTemplateSource(
            businessTripFileType === "leaves_during_trip" ? "custom" : "system"
          );
          setSystemTemplateFields([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSystemFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isBusinessTripCreateFlow, companyId, businessTripFileType]);

  useEffect(() => {
    if (!id) return;
    async function fetchData() {
      try {
        const {
          date,
          templateId: templateId_,
          signType,
          filesTemplateCustomField,
          fileTypeContext,
          signersList,
          businesstrip,
        } = await fetchById(id, companyId);
        const editBranch = resolveEditBranch(
          templateId_?.id,
          businessTripFileType,
          companyId
        );
        if (editBranch === "template") {
          const fetchedTemplateId = templateId_?.id;
          setTemplateId(fetchedTemplateId);
          setTemplateIdByFetch(fetchedTemplateId);

          const fileTemplateEntity = await fetchFileTemplateById(
            companyId,
            fetchedTemplateId
          );
          setTemplateEntity(fileTemplateEntity);
          const transformedFields = await transformCustomFields(
            filesTemplateCustomField?.customFields,
            fileTemplateEntity.customFields
          );
          const loadedCustomFields = fileTemplateEntity.customFields.map(
            (field: CustomField) => ({
              ...field,
              value:
                field.type === "file"
                  ? transformedFields[field.label] || []
                  : transformedFields[field.label] || "",
            })
          );
          setCustomFields(loadedCustomFields);
          setInitialCustomFieldsWhenEditLoaded(loadedCustomFields);
          setInitialTemplateEntityWhenEditLoaded(fileTemplateEntity);
          setProducts(filesTemplateCustomField?.products || []);
          setProductGroups(
            (filesTemplateCustomField as any)?.productGroups || []
          );
          setBusinessTripId(businesstrip?.id || null);
        } else if (editBranch === "system-template") {
          setIsEditingSystemTemplate(true);
          try {
            const sysRes = await axios.get(
              `${API_URL}/${companyId}/system-template-fields/${businessTripFileType}`
            );
            const sysFields: CustomField[] = sysRes.data.fields || [];
            if (sysFields.length > 0) {
              const savedValues =
                filesTemplateCustomField?.customFields || {};
              setCustomFields(
                sysFields.map((f: any) => ({
                  ...f,
                  value: savedValues[f.label] ?? (f.isMulti ? [] : ""),
                }))
              );
            }
          } catch (_e) {
            // Registry unavailable
          }
          setBusinessTripId(businesstrip?.id || null);
        }
        setSignType(signType);
        setDate(date);
        setFileTypeContext(fileTypeContext);
        hydrateSigners(signersList);
      } catch (_e) {
        // silently ignore
      }
    }
    fetchData();
  }, [id, companyId]);

  const parentTemplateId =
    !id && !lockedTemplateId ? normalizeBuildPdfTemplateId(template) : "";

  const isFormValid = () => {
    let validationErrors = validateForm({
      templateId,
      date,
      subject,
      selectedContactIds,
      signType,
      selectedUser,
      selectedNonTechUser,
      isSystemSource,
      isEditingSystemTemplate,
      isAutoBusinessTripDocument,
      customFields,
      products,
      productGroups,
    });
    const parentMismatch = getParentTemplateMismatchErrorKey({
      templateId,
      parentTemplateId,
      isSystemSource,
      isEditingSystemTemplate,
    });
    if (parentMismatch) {
      validationErrors = { ...validationErrors, templateId: parentMismatch };
    }
    const translatedErrors: { [key: string]: string } = {};
    for (const [key, val] of Object.entries(validationErrors)) {
      translatedErrors[key] =
        typeof val === "string" ? t(val) : t(val.key, val.params);
    }
    setErrors(translatedErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) return;
    const formData = buildPayload({
      date,
      templateId,
      customerId,
      customFields,
      products,
      productGroups,
      fileId,
      selectedContactIds,
      selectedUser,
      selectedNonTechUser,
      signType,
      selectedTabUser,
      fileTypeContext,
      user,
      orderId: null,
      fuelReportId: null,
      businessTripId,
      businessTripFileType,
    });

    if (!companyId) return;
    try {
      setIsFailed(false);
      setIsLoading(true);
      const action = id ? updateDocument : submitDocument;
      const fileEntity = await action(companyId, formData, id);
      const savedFileId = fileEntity?.id ?? fileEntity?.data?.id;
      if (fileId) dispatch(fetchFileDetails({ fileId, companyId }));
      if (savedFileId) {
        if (user) dispatch(fetchUserDetails({ userId: user, companyId }));
        dispatch(fetchCategoryDetails({ companyId, source: "profile" }));
        dispatch(fetchFileDetails({ fileId: savedFileId, companyId }));
        dispatch(
          fetchAuthorizedFileDetails({ fileId: savedFileId, companyId })
        );
        if (companyId && businessTripId) {
          dispatch(
            fetchBusinessTripDetails({ companyId, tripId: businessTripId })
          );
        }
        setIsLoading(false);
        setIsFailed(false);
        setIsSuccessful(true);
        setTimeout(() => {
          dispatch(setIsModalxOpen(false));
        }, 1500);
      } else {
        setIsFailed(true);
        setIsLoading(false);
      }
    } catch (_e) {
      setIsFailed(true);
      setIsLoading(false);
    }
  };

  return (
    <DocumentFormShell
      onSubmit={handleFormSubmit}
      isLoading={isLoading}
      isFailed={isFailed}
      isSuccessful={isSuccessful}
      submitDisabled={loadingSystemFields}
    >
      {loadingSystemFields && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <Spinner size="small" />
        </div>
      )}
      {!lockedTemplateId &&
        !isSystemSource &&
        !isEditingSystemTemplate &&
        !loadingSystemFields &&
        !(id && !templateId) && (
          <>
            <FileTemplateSelect
              onChange={setTemplateId}
              value={templateId}
              isRequired={true}
              isCustomerApproved={isCustomerApproved}
              templateId={
                id ? "" : normalizeBuildPdfTemplateId(template) || templateIdByFetch
              }
              error={errors["templateId"]}
              subject={subject}
            />
            {templateId && (
              <TextField
                label={t("file_context")}
                type="text"
                autoComplete="off"
                value={fileTypeContext}
                onChange={setFileTypeContext}
                labelHidden
                placeholder={t("enter_file_context")}
              />
            )}
          </>
        )}
      {(templateId || hasSystemFields || isEditingSystemTemplate) &&
        !isAutoBusinessTripDocument && (
          <DatePickerx
            disableDatesAfter={null}
            title={t("document_date")}
            isRequired={true}
            onChange={(value: any) =>
              setDate(value.toISOString().split("T")[0])
            }
            selected={date}
            readOnly={true}
          />
        )}
      {!isAutoBusinessTripDocument && (
        <CustomFieldsRenderer
          customFields={customFields}
          errors={errors}
          onFieldChange={handleCustomFieldChange}
          products={products}
          productGroups={productGroups}
          setProducts={setProducts}
          setProductGroups={setProductGroups}
          orderId={null}
          templateId={templateId}
          customerId={customerId}
          fileEntitiesMap={fileEntitiesMap}
        />
      )}
    </DocumentFormShell>
  );
}
